import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Badge,
  Box,
  ButtonSelect,
  Checkbox,
  Icon,
  Menu,
  TabContent,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { AuditLogColumnFilterBodyNote } from '../../../shared/components/AuditLogViewer';
import { CF_NEU_STL } from '../../../shared/constants';
import {
  useInvertedLocalize,
  useLocalize,
  useTypedSelector,
} from '../../../shared/hooks';

import AuditLogColumnSortableItem from './AuditLogColumnSortableItem/AuditLogColumnSortableItem';
import { FIXED_AUDIT_COLUMNS } from './constants';
import type {
  AppliedColumnFilters,
  AuditLogColumnFilters,
  AuditLogColumnOption,
} from './types';
import { getActiveFiltersForColumns } from './utils';

import './AuditLogColumnFilter.css';

// Types for audit columns API items and keys
interface AuditColumnItem {
  id: string;
  localizationKey: string;
  isDefault: boolean;
  columnOrder?: number;
  isColumnVisible?: boolean;
}
type ApiKey =
  | 'securityAuditLog'
  | 'changeAuditLog'
  | 'tcodeAuditLog'
  | 'systemAuditLog';

interface AuditLogColumnFilterProps {
  columnFilters: AuditLogColumnFilters;
  setColumnFilters: (filters: AuditLogColumnFilters) => void;
  appliedColumnFilters: AppliedColumnFilters;
  setAppliedColumnFilters: (filters: AppliedColumnFilters) => void;
  isButtonKindIcon?: boolean;
  isDisabled?: boolean;
  onColumnChange?: (appliedFilters: AppliedColumnFilters) => void;
}

const MIN_COUNT_FOR_TOOLTIP = 30;
const TOOLTIP_DELAY_IN_MS = 500;

/**
 * AuditLogColumnFilter component for managing column visibility in the audit log viewer
 */
function AuditLogColumnFilter({
  columnFilters,
  setColumnFilters,
  appliedColumnFilters,
  setAppliedColumnFilters,
  isButtonKindIcon = false,
  isDisabled = false,
  onColumnChange,
}: AuditLogColumnFilterProps): React.ReactElement {
  const [showBadgeOnColButton, setShowBadgeOnColButton] = useState(false);
  const [activeTab, setActiveTab] = useState('SEC_AUDIT_LOG');

  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggingItemIndex, setDraggingItemIndex] = useState<number | null>(
    null
  );
  const [draggingItemScrollAmount, setDraggingItemScrollAmount] = useState(0);

  const localize = useLocalize();
  const l = useInvertedLocalize();

  // English labels for group labels (mapped to activitylog.* keys via useInvertedLocalize)
  const groupLabelMap: Record<string, string> = useMemo(
    () => ({
      SEC_AUDIT_LOG: 'Security Audit Log',
      CHANGE_LOG: 'Change Log',
      TCODE_LOG: 'T-Code Log',
      SYSTEM_LOG: 'System Log',
    }),
    []
  );

  // Register audit-viewer-specific messages into the localization system, if needed
  // Consumers can register audit viewer messages separately via localization bootstrapping.

  // Get columns data and loading state from Redux (populated by AuditLogViewerBody)
  const fetchedColumns = useTypedSelector((s) => s.aag.auditLog.fetchedColumns);

  // Apply API columns once on first load; do not reset on open
  const isApiAppliedRef = useRef(false);

  useEffect(() => {
    if (isApiAppliedRef.current) {
      return;
    }

    const {
      securityAuditLog = [],
      changeAuditLog = [],
      tcodeAuditLog = [],
      systemAuditLog = [],
    } = (fetchedColumns ?? {}) as unknown as Record<string, AuditColumnItem[]>;

    const groupsMap: Record<ApiKey, AuditColumnItem[]> = {
      securityAuditLog,
      changeAuditLog,
      tcodeAuditLog,
      systemAuditLog,
    };

    const hasData =
      Array.isArray(securityAuditLog) ||
      Array.isArray(changeAuditLog) ||
      Array.isArray(tcodeAuditLog) ||
      Array.isArray(systemAuditLog);

    const apiToGroupMap = {
      securityAuditLog: { label: 'Security Audit Log', value: 'SEC_AUDIT_LOG' },
      changeAuditLog: { label: 'Change Log', value: 'CHANGE_LOG' },
      tcodeAuditLog: { label: 'T-Code Log', value: 'TCODE_LOG' },
      systemAuditLog: { label: 'System Log', value: 'SYSTEM_LOG' },
    } as const;

    if (!hasData) {
      return;
    }

    const normalizedFilters: AuditLogColumnFilters = (
      Object.keys(apiToGroupMap) as ApiKey[]
    )
      .filter(
        (apiKey) =>
          Array.isArray(groupsMap[apiKey]) && groupsMap[apiKey].length > 0
      )
      .map((apiKey) => {
        const group = apiToGroupMap[apiKey];
        const source = groupsMap[apiKey];

        const options: AuditLogColumnOption[] = source
          .filter((c) => c.isColumnVisible !== false) // Only show columns where isColumnVisible is not false
          .map((c) => ({
            label: localize(c.localizationKey),
            value: `${group.value}-${c.id}`,
            isChecked: c.isDefault,
            columnOrder: c.columnOrder ?? 999, // Default high order for items without columnOrder
          }))
          .sort((a, b) => {
            // First sort by checked status (checked items at top), then by columnOrder
            if (a.isChecked !== b.isChecked) {
              return Number(b.isChecked) - Number(a.isChecked);
            }

            const orderA = a.columnOrder || 999;
            const orderB = b.columnOrder || 999;

            return orderA - orderB;
          });

        return {
          label: groupLabelMap[group.value]
            ? l(groupLabelMap[group.value])
            : group.label,
          value: group.value,
          isDisabled: false,
          options,
        };
      });

    if (normalizedFilters.length > 0) {
      setColumnFilters(normalizedFilters);
      const newApplied = getActiveFiltersForColumns(normalizedFilters);

      setAppliedColumnFilters(newApplied);

      if (onColumnChange) {
        onColumnChange(newApplied);
      }

      isApiAppliedRef.current = true;
    }
  }, [
    fetchedColumns,
    localize,
    groupLabelMap,
    l,
    setColumnFilters,
    setAppliedColumnFilters,
    onColumnChange,
  ]);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const dropDownBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Keep the current tab unless it's missing or disabled
    const currentIsValid = columnFilters.some(
      (f) => f.value === activeTab && !f.isDisabled
    );

    if (!currentIsValid) {
      const firstAvailable = columnFilters.find((f) => !f.isDisabled);

      if (firstAvailable && activeTab !== firstAvailable.value) {
        setActiveTab(firstAvailable.value);
      }
    }
  }, [columnFilters, activeTab]);

  // Check if any columns are customized from default
  const hasCustomizedColumns = useMemo(
    () =>
      Object.values(appliedColumnFilters).some((columns) => columns.length > 0),
    [appliedColumnFilters]
  );

  useEffect(() => {
    setShowBadgeOnColButton(hasCustomizedColumns);
  }, [hasCustomizedColumns]);

  // Handle column checkbox change
  const handleChange = useCallback(
    (optionValue: string) => {
      const modifiedFilters = columnFilters.map((filter) => {
        const modifiedFilter = {
          ...filter,
          options: filter.options
            .map((option) => {
              if (
                option.value === optionValue &&
                !FIXED_AUDIT_COLUMNS.includes(optionValue)
              ) {
                return { ...option, isChecked: !option.isChecked };
              }

              return option;
            })
            .sort((a, b) => {
              // Keep checked columns at top, then maintain original order
              if (a.isChecked !== b.isChecked) {
                return Number(b.isChecked) - Number(a.isChecked);
              }

              const orderA = a.columnOrder ?? 999;
              const orderB = b.columnOrder ?? 999;

              return orderA - orderB;
            }),
        };

        return modifiedFilter;
      });

      setColumnFilters(modifiedFilters);
      const newAppliedFilters = getActiveFiltersForColumns(modifiedFilters);

      setAppliedColumnFilters(newAppliedFilters);

      if (onColumnChange) {
        onColumnChange(newAppliedFilters);
      }
    },
    [columnFilters, setColumnFilters, setAppliedColumnFilters, onColumnChange]
  );

  // Handle tab change
  const handleActiveTab = (value: string) => {
    if (value) {
      if (dropDownBodyRef.current) {
        dropDownBodyRef.current.scrollTop = 0;
      }

      setActiveTab(value);
    }
  };

  // Drag and drop handlers
  const findItemIndex = useCallback(
    (itemId: string): number | null => {
      let foundIndex = null;

      columnFilters.some((filter) => {
        const filteredOptions = filter.options.filter(
          (opt) => !FIXED_AUDIT_COLUMNS.includes(opt.value)
        );
        const idx = filteredOptions.findIndex((opt) => opt.value === itemId);

        if (idx !== -1) {
          foundIndex = idx;

          return true;
        }

        return false;
      });

      return foundIndex;
    },
    [columnFilters]
  );

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const scrollAmount = dropDownBodyRef.current?.scrollTop ?? 0;
      const index = findItemIndex(String(active.id));

      setDraggedItem(String(active.id));
      setDraggingItemScrollAmount(scrollAmount);
      setDraggingItemIndex(index);
    },
    [findItemIndex]
  );

  const reorderColumnList = useCallback(
    (srcIndex: number, destIndex: number, parentFilter: string) => {
      const reorderedList = columnFilters.map((filter) => {
        if (filter.value === parentFilter) {
          const tempOptions = filter.options.filter(
            (option) => !FIXED_AUDIT_COLUMNS.includes(option.value)
          );
          const [removedOption] = tempOptions.splice(srcIndex, 1);
          const modifiedOptions = [
            ...filter.options.filter((option) =>
              FIXED_AUDIT_COLUMNS.includes(option.value)
            ),
            ...tempOptions.slice(0, destIndex),
            removedOption,
            ...tempOptions.slice(destIndex),
          ];

          // Separate checked and unchecked items while preserving drag order
          const checkedItems = modifiedOptions.filter((opt) => opt.isChecked);
          const uncheckedItems = modifiedOptions.filter(
            (opt) => !opt.isChecked
          );

          const finalOptions = [...checkedItems, ...uncheckedItems];

          return { ...filter, options: finalOptions };
        }

        return filter;
      });

      setColumnFilters(reorderedList);
      const newAppliedFilters = getActiveFiltersForColumns(reorderedList);

      setAppliedColumnFilters(newAppliedFilters);

      if (onColumnChange) {
        onColumnChange(newAppliedFilters);
      }
    },
    [columnFilters, setColumnFilters, setAppliedColumnFilters, onColumnChange]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      let parentFilter: string | undefined;
      let srcIndex: number | undefined;
      let destIndex: number | undefined;

      columnFilters.find((filter) => {
        const filteredOptions = filter.options.filter(
          (opt) => !FIXED_AUDIT_COLUMNS.includes(opt.value)
        );
        const activeIndex = filteredOptions.findIndex(
          (opt) => opt.value === String(active.id)
        );

        if (activeIndex !== -1) {
          parentFilter = filter.value;
          srcIndex = activeIndex;
          destIndex = filteredOptions.findIndex(
            (opt) => opt.value === String(over.id)
          );

          return true;
        }

        return false;
      });

      if (
        parentFilter !== undefined &&
        srcIndex !== undefined &&
        destIndex !== undefined
      ) {
        reorderColumnList(srcIndex, destIndex, parentFilter);
      }
    },
    [columnFilters, reorderColumnList]
  );

  const handleDragEndWrapper = useCallback(
    (event: DragEndEvent) => {
      setDraggedItem(null);
      setDraggingItemIndex(null);
      setDraggingItemScrollAmount(0);
      handleDragEnd(event);
    },
    [handleDragEnd]
  );

  // Tooltip helpers
  const getTooltipText = useCallback(
    (option: AuditLogColumnOption): string | null =>
      option.label.length > MIN_COUNT_FOR_TOOLTIP ? option.label : null,
    []
  );

  const getTooltipTrigger = useCallback(
    (option: AuditLogColumnOption): React.ReactElement => (
      <Typography
        kind='body2'
        className='AuditLogColumnFilter-checkBoxText'
        color={CF_NEU_STL}
        lineLimit={1}>
        {option.label}
      </Typography>
    ),
    []
  );

  // Get dragged option for overlay
  const draggedOption = draggedItem
    ? columnFilters
        .flatMap((filter) => filter.options)
        .find((o) => o.value === draggedItem)
    : null;

  const overlayStyle = {
    position: 'fixed' as const,
    top:
      draggingItemIndex !== null
        ? draggingItemIndex * 40 + 120 - draggingItemScrollAmount
        : 0,
    left: 0,
    opacity: 1,
  };

  // Tab groups component
  const getTabGroups = (): React.ReactElement => (
    <Box className='AuditLogColumnFilter-tabGroup-frame'>
      <TabGroup
        value={activeTab}
        onChange={handleActiveTab}
        className='AuditLogColumnFilter-dropdownTabGroup'>
        {columnFilters.map((tab) => (
          <TabItem
            key={tab.value}
            label={
              groupLabelMap[tab.value] ? l(groupLabelMap[tab.value]) : tab.label
            }
            value={tab.value}
            size='medium'
          />
        ))}
      </TabGroup>
    </Box>
  );

  // Main filter content
  const getColumnFilterContent = (): React.ReactElement => (
    <div className='AuditLogColumnFilter-dropDownBody'>
      {getTabGroups()}
      <div
        ref={dropDownBodyRef}
        className='AuditLogColumnFilter-dropDownScrollArea'>
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEndWrapper}>
          <AuditLogColumnFilterBodyNote />
          <div className='AuditLogColumnFilter-filterList'>
            {columnFilters.map((tab) => (
              <TabContent
                value={activeTab}
                tabValue={tab.value}
                key={tab.value}>
                <SortableContext
                  items={tab.options
                    .filter(
                      (option) => !FIXED_AUDIT_COLUMNS.includes(option.value)
                    )
                    .map((option) => option.value)}
                  strategy={verticalListSortingStrategy}>
                  <div className='AuditLogColumnFilter-listContainer'>
                    {tab.options
                      .filter(
                        (option) => !FIXED_AUDIT_COLUMNS.includes(option.value)
                      )
                      .map((option) => (
                        <AuditLogColumnSortableItem
                          key={option.value}
                          option={option}
                          handleChange={handleChange}
                          getTooltipText={getTooltipText}
                          getTooltipTrigger={getTooltipTrigger}
                        />
                      ))}
                  </div>
                </SortableContext>
              </TabContent>
            ))}
          </div>
          <DragOverlay style={overlayStyle}>
            {draggedOption ? (
              <div className='AuditLogColumnFilter-dragOverlay'>
                {draggedOption.isChecked ? (
                  <div className='AuditLogColumnFilter-dragger'>
                    <Tooltip
                      isPortalEnabled
                      text={l('Drag to Reorder')}
                      trigger={
                        <Icon
                          kind='DragVertical'
                          color='neutral-600'
                          size='smallMedium'
                        />
                      }
                    />
                  </div>
                ) : (
                  <span className='AuditLogColumnFilter-draggerFiller' />
                )}
                <Typography
                  lineLimit={3}
                  kind='label'
                  htmlFor={draggedOption.value}
                  className='AuditLogColumnFilter-checkBox'>
                  <Checkbox
                    id='audit-column-filter-checkbox'
                    name={draggedOption.value}
                    isChecked={draggedOption.isChecked}
                    dataTestId='checkbox-no-hovereffect'
                  />
                  <Tooltip
                    trigger={getTooltipTrigger(draggedOption)}
                    openDelay={TOOLTIP_DELAY_IN_MS}
                    text={getTooltipText(draggedOption) ?? undefined}
                    maxWidth='20rem'
                  />
                </Typography>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );

  return (
    <Menu
      ref={menuRef}
      CustomMenu={getColumnFilterContent()}
      setIsExpanded={() => {
        if (dropDownBodyRef.current) {
          dropDownBodyRef.current.scrollTop = 0;
        }
        // Do not re-apply defaults on open; state persists in Redux
      }}
      crossAxisOffset={0}
      placement='bottom-end'
      fallbackPlacements={['bottom-start', 'bottom']}
      trigger={
        <ButtonSelect
          kind={isButtonKindIcon ? 'icon' : 'rectangle'}
          label={l('Columns')}
          size='medium'
          BadgeComp={
            showBadgeOnColButton ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : undefined
          }
          prefixIcon={
            <Icon kind='Columns' color='neutral-600' size='smallMedium' />
          }
          isDisabled={
            isDisabled ||
            columnFilters.length === 0 ||
            !columnFilters.some((f) => f.options.length > 0)
          }
          isVisualCueEnabled={false}
          className='AuditLogColumnFilter-button'
        />
      }
      className='AuditLogColumnFilter'
    />
  );
}

AuditLogColumnFilter.defaultProps = {
  isButtonKindIcon: false,
  isDisabled: false,
  onColumnChange: undefined,
};

export default AuditLogColumnFilter;
